import { useEffect, useState } from 'react'
import { addDays, addMonths, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns'
import { it } from 'date-fns/locale'
import api from '../api'
import DashboardDayAgenda, { lessonDate } from './DashboardDayAgenda'
import styles from './DashboardCalendar.module.css'

const weekdays = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica']
export default function DashboardCalendar() {
    const [month, setMonth] = useState(() => startOfMonth(new Date()))
    const [selected, setSelected] = useState(() => format(new Date(), 'yyyy-MM-dd'))
    const [lessons, setLessons] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [retry, setRetry] = useState(0)
    useEffect(() => {
        let active = true
        setLoading(true)
        setError(false)
        const params = new URLSearchParams({
            exclude_hidden_editions: 'true',
            start_after: month.toISOString().replace('Z', ''),
            end_before: endOfMonth(month).toISOString().replace('Z', ''),
        })
        const load = (background = false) => api.get(`/admin/lessons?${params}`).then(result => {
            if (active) setLessons(result)
        }).catch(() => { if (active && !background) { setLessons([]); setError(true) } })
            .finally(() => { if (active) setLoading(false) })
        load()
        const timer = setInterval(() => load(true), 60000)
        return () => { active = false; clearInterval(timer) }
    }, [month, retry])
    const grouped = {}
    lessons.forEach(lesson => {
        const start = lessonDate(lesson.starts_at)
        const key = Number.isNaN(start.getTime()) ? null : format(start, 'yyyy-MM-dd')
        if (key) (grouped[key] ||= []).push(lesson)
    })
    const days = []
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 })
    for (let day = startOfWeek(month, { weekStartsOn: 1 }); day <= end; day = addDays(day, 1)) days.push(day)
    const today = format(new Date(), 'yyyy-MM-dd')
    const navigateMonth = delta => {
        const next = startOfMonth(addMonths(month, delta))
        setMonth(next)
        setSelected(format(next, 'yyyy-MM-dd'))
    }
    return <section className={styles.calendarShell} aria-label="Calendario delle lezioni">
        <div className={styles.calendar}>
        <div className={styles.toolbar}>
            <div><h3 aria-live="polite">{format(month, 'MMMM yyyy', { locale: it })}</h3><p>{loading ? 'Caricamento…' : error ? 'Calendario non disponibile' : `${lessons.length} lezioni in programma`}</p></div>
            <div className={styles.controls}>
                <button type="button" onClick={() => { setMonth(startOfMonth(new Date())); setSelected(today) }}>Oggi</button>
                <button type="button" aria-label="Mese precedente" onClick={() => navigateMonth(-1)}>‹</button>
                <button type="button" aria-label="Mese successivo" onClick={() => navigateMonth(1)}>›</button>
            </div>
        </div>
        <table className={styles.grid} aria-busy={loading}>
            <caption className={styles.srOnly}>Lezioni di {format(month, 'MMMM yyyy', { locale: it })}</caption>
            <thead><tr>{weekdays.map(day => <th scope="col" key={day}><abbr title={day}>{day.slice(0, 3)}</abbr></th>)}</tr></thead>
            <tbody>{Array.from({ length: days.length / 7 }, (_, week) => <tr key={week}>{days.slice(week * 7, week * 7 + 7).map(day => {
                const key = format(day, 'yyyy-MM-dd')
                const outside = day.getMonth() !== month.getMonth()
                const count = loading || outside ? 0 : (grouped[key]?.length || 0)
                return <td key={key} className={outside ? styles.otherMonth : ''}>
                    <button type="button" className={`${styles.day} ${key === today ? styles.today : ''} ${key === selected ? styles.selected : ''}`}
                        aria-label={`${format(day, 'EEEE d MMMM yyyy', { locale: it })}${!loading && !outside ? `, ${count} lezioni` : ''}`}
                        aria-current={key === today ? 'date' : undefined} aria-pressed={key === selected}
                        onClick={() => { setSelected(key); if (outside) setMonth(startOfMonth(day)) }}>{day.getDate()}</button>
                    <span className={styles.marker} aria-hidden="true">{count > 0 ? `${count} lez.` : '\u00a0'}</span>
                </td>
            })}</tr>)}</tbody>
        </table>
        <div className={styles.agenda} aria-live="polite">
            <h4>{format(new Date(`${selected}T12:00:00`), 'EEEE d MMMM', { locale: it })}</h4>
            {loading ? <p>Caricamento delle lezioni…</p> : error ? <div><p>Impossibile caricare le lezioni.</p><button type="button" className={styles.retry} onClick={() => setRetry(value => value + 1)}>Riprova</button></div> :
                <DashboardDayAgenda lessons={lessons} day={selected} />}
        </div>
        </div>
    </section>
}
